import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTimer } from 'react-timer-hook';

import { AuthApi } from '../../api';
import { LoadingOverlay } from '../../components/atoms';
import {
  ClientLoginFormFields,
  useAuthContext,
  useClientLoginForm,
} from '../../hooks';
import { normalizePhoneNumber } from '../../utils';

function getExpiryTimestamp(): Date {
  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + 59);
  return expiryTimestamp;
}

interface Props {
  onBack(): void;
}

export const ClientLoginFormSecondStep: FC<Props> = ({ onBack }) => {
  const [code, setCode] = useState('');
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const { signIn } = useAuthContext();
  const { minutes, seconds, restart } = useTimer({
    expiryTimestamp: getExpiryTimestamp(),
  });
  const { handleSubmit, watch } = useClientLoginForm();
  const phoneNumber = watch('phoneNumber');

  const onSubmit: SubmitHandler<ClientLoginFormFields> = useCallback(
    async ({ phoneNumber: formPhoneNumber }) => {
      const phone = formPhoneNumber || phoneNumber;
      if (!phone || !code) {
        return;
      }
      try {
        setIsValidatingCode(true);
        const response = await AuthApi.validateCode(
          normalizePhoneNumber(phone),
          code,
        );
        // userId приходит из validate_code — сессию/куку не проверяем здесь,
        // иначе при кратковременном 403/401 после логина пользователь не сможет войти,
        // а код станет одноразовым.
        signIn(response.data.userId, true, {
          isProfileCompleted: response.data.isProfileCompleted,
          legal_agreement_accepted: response.data.legal_agreement_accepted,
          legal_agreement_version: response.data.legal_agreement_version,
        });
      } catch (error) {
        // Ошибка обрабатывается автоматически
      } finally {
        setIsValidatingCode(false);
      }
    },
    [signIn, phoneNumber, code],
  );

  const handleChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
    setCode(digitsOnly);
  };

  const isValid = useMemo(() => code.replace(/\D/g, '').length === 4, [code]);

  // Обработка клавиатуры: Enter для продолжения, Backspace для возврата
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Если фокус в поле ввода, обрабатываем Enter только если это не Shift+Enter
        if (
          event.key === 'Enter' &&
          !event.shiftKey &&
          !event.ctrlKey &&
          !event.metaKey
        ) {
          event.preventDefault();
          if (isValid && !isValidatingCode) {
            handleSubmit(onSubmit)();
          }
        }
        // Backspace в пустом поле - возврат назад
        if (
          event.key === 'Backspace' &&
          code === '' &&
          !event.shiftKey &&
          !event.ctrlKey &&
          !event.metaKey
        ) {
          event.preventDefault();
          onBack();
        }
        return;
      }

      // Enter - продолжить
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        if (isValid && !isValidatingCode) {
          handleSubmit(onSubmit)();
        }
      }

      // Backspace - назад
      if (
        event.key === 'Backspace' &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isValid, isValidatingCode, code, onBack, handleSubmit]);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="relative m-auto flex w-full flex-col items-center rounded-[24px] bg-white px-[24px] py-8 md:w-[720px] lg:px-12"
    >
      {isValidatingCode && <LoadingOverlay />}
      <div className="mb-[32px] flex w-full flex-col items-center gap-[16px]">
        <h1 className="text-center text-[30px] font-medium leading-[36px] text-[#151515]">
          Введите код подтверждения
        </h1>
        <p className="text-center text-[16px] font-normal leading-[20px] text-[#151515] opacity-50">
          Отправили на {phoneNumber}
        </p>
      </div>
      {/*  @ts-ignore */}
      {/* <CodeField /> */}
      <div className="relative flex h-14 w-full items-center gap-3 rounded-xl border border-[#afc4d1] p-[18px]">
        <input
          ref={codeInputRef}
          type="text"
          inputMode="numeric"
          value={code}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
            handleChange(value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && code.length > 0) {
              e.preventDefault();
              handleChange(code.slice(0, -1));
            }
          }}
          maxLength={4}
          className="absolute left-[18px] w-full bg-transparent text-base font-normal leading-5 text-transparent caret-[#151515] outline-none"
          placeholder=""
          autoComplete="off"
          style={{
            width: 'calc(100% - 2.5rem)',
            fontFamily: 'monospace',
          }}
        />
        <div
          className="pointer-events-none flex items-center text-base font-normal leading-5 text-[#151515]"
          style={{ letterSpacing: '12px' }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <span key={index} className="inline-block w-[1ch] text-center">
              {code[index] || <span className="text-[#151515]/50">*</span>}
            </span>
          ))}
        </div>
      </div>
      <div className="mb-[24px] mt-[16px] text-center">
        {seconds > 0 ? (
          <>
            <span className="w-full text-center text-base leading-5  text-[#151515] transition disabled:opacity-50">
              отправить повторно через
            </span>
            &nbsp;{String(minutes).padStart(2, '0')}:
            {String(seconds).padStart(2, '0')}
          </>
        ) : (
          <button
            className="opacity: 0.5; w-full text-center  text-base font-bold leading-5 text-[#0788f1] transition disabled:opacity-50"
            onClick={() => restart(getExpiryTimestamp())}
            type="button"
          >
            Выслать код повторно
          </button>
        )}
      </div>
      <div className="flex w-full items-start gap-[16px]">
        <button
          type="button"
          onClick={onBack}
          className="relative flex-[0_0_auto]"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="60"
            height="60"
            viewBox="0 0 60 60"
            fill="none"
          >
            <rect
              width="60"
              height="60"
              rx="12"
              fill="#0788F1"
              fillOpacity="0.08"
            />
            <path
              d="M37.625 30H23.875"
              stroke="#0788F1"
              strokeWidth="1.6"
              strokeLinecap="square"
              strokeLinejoin="round"
            />
            <path
              d="M28.625 24.375L23 30L28.625 35.625"
              stroke="#0788F1"
              strokeWidth="1.6"
              strokeLinecap="square"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          disabled={!isValid || isValidatingCode}
          onClick={handleSubmit(onSubmit)}
          className="flex h-[60px] w-full items-center justify-center rounded-md bg-[#0788f1] p-[15px] text-base font-bold text-white transition-opacity disabled:opacity-50"
        >
          Войти
        </button>
      </div>
    </form>
  );
};
